import { Brand, Platform, Environment } from 'src/environment';
import { storageUtilityFactory } from 'src/shared/utilities/storageUtilityFactory';
import { StorageType } from 'src/shared/utilities/storage.utility';

export const ENVIRONMENT_LOCAL_STORAGE_KEY = 'hybrid-environmentVariables';

const PREPROD_REGEX = /[-]*pp/;
const RC_SUBDOMAIN = 'rc';
const PLATFORM_TRANSFORMS: { [subDomain: string]: Platform } = {
  [RC_SUBDOMAIN]: Platform.WEB,
  ios: Platform.IOS,
  android: Platform.ANDROID,
};
export interface EnvironmentVariables extends Object {
  brand: Brand;
  platform: Platform;
  environment: Environment;
  partner?: string;
}

export function saveEnvironmentVariables(
  envVariables: Partial<EnvironmentVariables>,
): void {
  const environmentVariablesToSave: Partial<EnvironmentVariables> = {
    ...envVariables,
  };
  delete environmentVariablesToSave.environment;

  const storage = storageUtilityFactory(StorageType.sessionStorage);
  storage.set(ENVIRONMENT_LOCAL_STORAGE_KEY, environmentVariablesToSave);
}

function getEnvironmentVariablesFromStorage(): Partial<EnvironmentVariables> {
  const storage = storageUtilityFactory(StorageType.sessionStorage);
  return storage.get(ENVIRONMENT_LOCAL_STORAGE_KEY) || {};
}

export function getEnvironmentVariables(): EnvironmentVariables {
  let parsedVars: EnvironmentVariables;
  if (isEnvPortBased()) {
    parsedVars = parsePortToEnvVariables();
  } else if (isEnvQueryStringBased()) {
    parsedVars = parseQueryStringToEnvVariables();
  } else {
    parsedVars = parseSubdomainToEnvVariables();
  }
  return {
    ...parsedVars,
    ...getEnvironmentVariablesFromStorage(),
  };
}

const defaultVariables: EnvironmentVariables = {
  brand: Brand.GRUBHUB,
  environment: Environment.DEVELOPMENT,
  platform: Platform.IOS,
};

function isEnvPortBased(): boolean {
  const { hostname } = window.location;

  return (
    hostname.includes('localhost') ||
    hostname.includes('localdev') ||
    hostname.includes('10.0.2.2')
  );
}

function isEnvQueryStringBased(): boolean {
  const { hostname } = window.location;
  return hostname.includes('ui-branches');
}

function parsePortToEnvVariables(): EnvironmentVariables {
  const port: number = +window.location.port;
  const rv: EnvironmentVariables = { ...defaultVariables };
  if (!(port % 2)) {
    rv.brand = Brand.SEAMLESS;
  }
  if (port >= 3009) {
    rv.environment = Environment.PROD;
  } else if (port >= 3007) {
    rv.environment = Environment.PREPROD;
  }
  if (port >= 3005) {
    rv.platform = Platform.WEB;
  } else if (port >= 3003) {
    rv.platform = Platform.ANDROID;
  } else {
    rv.platform = Platform.IOS;
  }
  return rv;
}

function parseQueryStringToEnvVariables(): EnvironmentVariables {
  const { search } = window.location;
  let rv: EnvironmentVariables;

  if (!search) {
    rv = defaultVariables;
  } else {
    rv = search
      .substring(1)
      .split('&')
      .reduce<EnvironmentVariables>(overrideEnvVariablesBySearchPart, {
        ...defaultVariables,
      });
  }

  return rv;
}

function overrideEnvVariablesBySearchPart(
  envVariables: EnvironmentVariables,
  searchPart: string,
): EnvironmentVariables {
  const [key, value] = searchPart.split('=');

  if (envVariables.hasOwnProperty(key)) {
    envVariables[key] = value;
  } else {
    console.warn(`Unknown search variable: ${key} (value: ${value})`);
  }

  return envVariables;
}

function parseSubdomainToEnvVariables(): EnvironmentVariables {
  return {
    environment: getEnvironmentBySubDomain(),
    platform: getPlatformBySubDomain(),
    brand: getBrandBySubdomain(),
    partner: getPartner(),
  };
}

function getSubdomain() {
  const { hostname } = window.location;
  const hostNameParts = hostname.split('.');

  return hostNameParts.length > 2 ? hostNameParts[0] : 'www';
}

function getPartner() {
  const subdomain = getSubdomain();
  const subdomainsToIgnore = [
    'www',
    'rc',
    'pp',
    'help',
    'android',
    'ios',
    'web',
  ];
  const cleanSubdomain = subdomain.replace(PREPROD_REGEX, '');
  const partner = cleanSubdomain.split('-')[0];

  return subdomainsToIgnore.includes(cleanSubdomain) ? undefined : partner;
}

function isRCEnvironment(): boolean {
  const subdomain = getSubdomain();

  return subdomain === RC_SUBDOMAIN;
}

function getEnvironmentBySubDomain(): Environment {
  let environment = Environment.PROD;

  if (isRCEnvironment()) {
    environment = Environment.RC;
  } else if (hasPreprodSubDomain()) {
    environment = Environment.PREPROD;
  }

  return environment;
}

function getPlatformBySubDomain(): Platform {
  const { hostname } = window.location;
  const hostNameParts = hostname.split('.');
  let rv = Platform.WEB;

  if (hostNameParts.length > 2) {
    const subdomain = hostNameParts[0];

    if (subdomain !== 'www') {
      const platformPart = subdomain.replace(PREPROD_REGEX, '').toLowerCase();
      rv = PLATFORM_TRANSFORMS[platformPart] ?? rv;
    }
  }

  return rv;
}

function hasPreprodSubDomain(): boolean {
  const { hostname } = window.location;
  const hostNameParts = hostname.split('.');

  return hostNameParts.length > 2 && PREPROD_REGEX.test(hostNameParts[0]);
}

function getBrandBySubdomain(): Brand {
  const { hostname } = window.location;
  const hostNameParts = hostname.split('.');
  const domainName =
    hostNameParts.length > 2 ? hostNameParts[1] : hostNameParts[0];

  return domainName.toUpperCase() === Brand.SEAMLESS
    ? Brand.SEAMLESS
    : Brand.GRUBHUB;
}
